import React from 'react';
import { format } from 'date-fns';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import CalendarIcon from '../../icons/CalendarIcon';
import { safeParse } from '../calendarDateUtils';
import { recurrenceCopy } from '../recurrenceCopy';
import type { CalendarEventDetail } from '../../../types/integrations';

interface EventWhenProps {
  event: CalendarEventDetail;
}

const sameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

const localTz = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const EventWhen: React.FC<EventWhenProps> = ({ event }) => {
  const start = safeParse(event.start);
  const end = safeParse(event.end);
  const userTz = localTz();
  const eventTz = event.start_timezone || null;
  const showTzHint = !event.all_day && eventTz && eventTz !== userTz;

  let primary: string;
  if (event.all_day) {
    primary = sameDay(start, end)
      ? `${format(start, 'EEEE, MMM d, yyyy')} · all day`
      : `${format(start, 'EEE, MMM d')} – ${format(end, 'EEE, MMM d, yyyy')} · all day`;
  } else if (sameDay(start, end)) {
    primary = `${format(start, 'EEEE, MMM d, yyyy')} · ${format(start, 'h:mm a')} – ${format(end, 'h:mm a')}`;
  } else {
    primary = `${format(start, 'EEE, MMM d, h:mm a')} – ${format(end, 'EEE, MMM d, h:mm a')}`;
  }

  const recurrence = recurrenceCopy(event.recurrence);

  return (
    <div className="flex items-start gap-3">
      <CalendarIcon className="w-5 h-5 text-text-tertiary mt-0.5 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-text-primary">{primary}</p>
        {(recurrence || showTzHint) && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5 text-xs text-text-tertiary">
            {recurrence && (
              <span className="inline-flex items-center gap-1">
                <ArrowPathIcon className="w-3 h-3" />
                {recurrence}
              </span>
            )}
            {showTzHint && <span>· {eventTz}</span>}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventWhen;
